// ConfirmDialog.tsx — the Axios replacement for window.confirm().
//
// Native dialogs are chrome the design system does not own: OS typeface, OS
// button order, no theme, no brand color, and they block the JS thread
// (DESIGN.md C-MSG-3). This is a react-bootstrap Modal with the same
// three-slot rhythm as every other modal in the system — Header / Body /
// Footer, cancel on the left, the committing action on the right.
//
// `useConfirm` keeps the migration from `confirm()` to a real dialog a
// one-line change at the call site:
//
//     const [confirm, confirmDialog] = useConfirm();
//     ...
//     if (!(await confirm('Delete this session?'))) return;
//     ...
//     return (<>{...page...}{confirmDialog}</>);
import React, { useCallback, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

export interface ConfirmOptions {
    /** Modal.Title. Defaults to "Confirm". Title Case, per DESIGN.md. */
    title?: string;
    /** Modal.Body content — the question, plus any consequence worth stating. */
    body: React.ReactNode;
    /** Label of the committing action. Defaults to "Confirm". */
    confirmLabel?: string;
    /** Label of the dismissing action. Defaults to "Cancel". */
    cancelLabel?: string;
    /**
     * Semantic variant of the committing button. `danger` for destructive and
     * irreversible actions, which is what most `confirm()` calls guard.
     */
    variant?: 'danger' | 'warning' | 'primary';
}

export interface ConfirmDialogProps extends ConfirmOptions {
    show: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/** Controlled confirm modal, for callers that already own the open state. */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    show,
    onConfirm,
    onCancel,
    title = 'Confirm',
    body,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'danger',
}) => (
    <Modal show={show} onHide={onCancel} centered>
        <Modal.Header closeButton>
            <Modal.Title className="h5 m-0">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{body}</Modal.Body>
        <Modal.Footer>
            {/* Tertiary action → btn-link, never a second filled button (C-BTN-3). */}
            <Button variant="link" onClick={onCancel}>
                {cancelLabel}
            </Button>
            <Button variant={variant} onClick={onConfirm} autoFocus>
                {confirmLabel}
            </Button>
        </Modal.Footer>
    </Modal>
);

/**
 * Promise-based confirm. Returns the asker and the element to render.
 *
 * The resolver is held in a ref so a pending `await` always settles exactly
 * once — closing via the header X, the cancel button, or the backdrop all
 * resolve `false`, matching `window.confirm()`'s contract.
 */
export function useConfirm(): [
    (options: ConfirmOptions | string) => Promise<boolean>,
    React.ReactElement,
] {
    const [options, setOptions] = useState<ConfirmOptions | null>(null);
    const resolver = useRef<((ok: boolean) => void) | null>(null);

    const settle = useCallback((ok: boolean) => {
        setOptions(null);
        const resolve = resolver.current;
        resolver.current = null;
        resolve?.(ok);
    }, []);

    const confirm = useCallback((opts: ConfirmOptions | string) => {
        // A pending dialog can only be replaced by resolving it false first,
        // otherwise its awaiter would hang forever behind the new one.
        resolver.current?.(false);
        setOptions(typeof opts === 'string' ? { body: opts } : opts);
        return new Promise<boolean>((resolve) => {
            resolver.current = resolve;
        });
    }, []);

    const element = (
        <ConfirmDialog
            show={options !== null}
            body={options?.body ?? null}
            title={options?.title}
            confirmLabel={options?.confirmLabel}
            cancelLabel={options?.cancelLabel}
            variant={options?.variant}
            onConfirm={() => settle(true)}
            onCancel={() => settle(false)}
        />
    );

    return [confirm, element];
}

export default ConfirmDialog;
